import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Columns3, LayoutGrid, List } from 'lucide-react';
import { Button, Card, Input, Typography, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';

import { updateDealStage, type DealListItem, type DealStage } from '../api/deals.client.js';
import { DealCardsGrid } from '../components/DealCardsGrid.js';
import { DealFormModal } from '../components/DealFormModal.js';
import { DealListView, DEAL_GRID_COLUMNS } from '../components/DealListView.js';
import { GridColumnsButton } from '../../../shared/grid/GridColumnsButton.js';
import { useGridProfile } from '../../../shared/grid/useGridProfile.js';
import { KanbanBoard } from '../components/KanbanBoard.js';
import { RevenueGoalBar } from '../components/RevenueGoalBar.js';
import { usePublishSelection } from '../../../shared/selection/useSelectionStore.js';
import { ProjectWorkspaceSurface } from '../components/ProjectWorkspaceSurface.js';
import { triggerCelebration } from '../../../shared/utils/celebration.js';
import { useDeals } from '../hooks/useDeals.js';
import { useDeleteDealMutation } from '../hooks/useDealMutations.js';
import { useDealStageMutation } from '../hooks/useDealStageMutation.js';
import { buildKanbanColumns, STAGE_ORDER } from '../lib/kanban.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { FilterBar } from '../../../shared/ui/FilterBar.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import {
  LIST_CREATE_BUTTON_STYLE,
  LIST_ICON_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
  SEG_TRACK_STYLE,
  segOptionStyle,
} from '../../../shared/ui/list-styles.js';
import { QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import {
  clearSelection,
  DEFAULT_DEAL_FILTERS,
  getFailedSelection,
  getActiveFilterCount,
  toDealListFilters,
  toggleSelection,
  type DealViewMode,
  type DealWorkbenchFilters,
} from '../lib/workbench.js';

const { Text } = Typography;

export function DealKanbanPage(): ReactElement {
  useI18nSubscription();
  const [params, setParams] = useSearchParams();
  const requestedView = params.get('view');
  const viewMode: DealViewMode =
    requestedView === 'list' || requestedView === 'cards' ? requestedView : 'kanban';
  const setViewMode = (view: DealViewMode): void => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set('view', view);
      return next;
    });
  };
  const [filters, setFilters] = useState<DealWorkbenchFilters>(DEFAULT_DEAL_FILTERS);
  // Отбор идёт на сервере: страница больше не забирает все сделки воркспейса,
  // чтобы отсеять их в браузере.
  const listFilters = useMemo(() => toDealListFilters(filters), [filters]);
  const { deals, isLoading, isError, error, refetch } = useDeals(listFilters);
  const stageMutation = useDealStageMutation();
  // Вид списка сделок: колонки настраиваются в режиме списка, канбан их не использует.
  const gridProfile = useGridProfile('deals', DEAL_GRID_COLUMNS);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchStage, setBatchStage] = useState<DealStage>('Contacted');
  const [batchError, setBatchError] = useState<string | null>(null);
  const [isBatchMoving, setIsBatchMoving] = useState(false);
  const [createStage, setCreateStage] = useState<DealStage | null>(null);
  const [editingDeal, setEditingDeal] = useState<DealListItem | null>(null);
  const deleteMutation = useDeleteDealMutation();

  // Публикуем выделение — быстрая заметка цепляется к отмеченным сделкам.
  usePublishSelection(
    'deal',
    deals
      .filter((deal) => selectedIds.has(deal.id))
      .map((deal) => ({ id: deal.id, label: deal.title })),
  );
  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [filters]);
  const locale = i18n.locale;
  const columns = useMemo(() => buildKanbanColumns(deals), [deals, locale]);
  const activeDealsCount = useMemo(
    () => deals.filter((deal) => deal.stage !== 'Won' && deal.stage !== 'Lost').length,
    [deals],
  );
  const needsAttentionCount = useMemo(
    () =>
      deals.filter(
        (deal) => deal.stage !== 'Won' && deal.stage !== 'Lost' && deal.nextActivityDue === null,
      ).length,
    [deals],
  );

  async function handleBatchMove(): Promise<void> {
    const ids = [...selectedIds];
    if (ids.length === 0) {
      return;
    }

    setIsBatchMoving(true);
    setBatchError(null);
    if (batchStage === 'Won') {
      triggerCelebration();
    }
    const results = await Promise.allSettled(
      ids.map((dealId) => updateDealStage(dealId, { toStage: batchStage })),
    );
    const failedIds = new Set(ids.filter((_, index) => results[index]?.status === 'rejected'));

    if (failedIds.size > 0) {
      setSelectedIds(getFailedSelection(ids, failedIds));
      setBatchError(
        i18n.t('crm.features.dynamic.features.deals.pages.dealkanbanpage.61', {
          p0: failedIds.size,
        }),
      );
    } else {
      setSelectedIds(clearSelection(selectedIds));
    }

    setIsBatchMoving(false);
    await refetch();
  }

  function openCreateForm(stage: DealStage): void {
    setCreateStage(stage);
  }

  const filterSearch = (
    <div className="flex-1 md:max-w-[400px]">
      <Input
        value={filters.query}
        onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
        data-testid="deals-search-input"
        aria-label={i18n.t('crm.features.features.deals.pages.dealkanbanpage.поиск_сделок')}
        placeholder={i18n.t(
          'crm.features.features.deals.pages.dealkanbanpage.поиск_по_сделке_или_контакту',
        )}
        allowClear
        variant="borderless"
        className="crm-radius-m bg-[var(--g-fill)] transition-colors"
        style={{ height: 28, fontSize: 12, padding: '0 10px' }}
      />
    </div>
  );
  const filterControls = (
    <div className="flex flex-wrap items-center gap-2 ml-auto">
      {/* Native select to keep playwright tests happy */}
      <select
        value={filters.stage}
        onChange={(event) =>
          setFilters((current) => ({
            ...current,
            stage: event.target.value as DealStage | 'all',
          }))
        }
        aria-label={i18n.t('crm.features.features.deals.pages.dealkanbanpage.фильтр_по_стадии')}
        className="bg-transparent text-[var(--ant-color-text)] text-[12px] font-medium transition-colors"
        style={{
          height: 28,
          border: '1px solid var(--ant-color-border)',
          borderRadius: 'var(--ant-border-radius, 10px)',
          padding: '0 10px',
          backgroundColor: 'var(--ant-color-bg-container)',
          outline: 'none',
          cursor: 'pointer',
        }}
      >
        <option value="all">
          {i18n.t('crm.features.features.deals.pages.dealkanbanpage.все_стадии')}
        </option>
        {STAGE_ORDER.map((stage) => (
          <option key={stage} value={stage}>
            {getEnumLabel('dealStage', stage, i18n.t)}
          </option>
        ))}
      </select>

      <select
        value={filters.activity}
        onChange={(event) =>
          setFilters((current) => ({
            ...current,
            activity: event.target.value as DealWorkbenchFilters['activity'],
          }))
        }
        aria-label={i18n.t('crm.features.features.deals.pages.dealkanbanpage.фильтр_по_активности')}
        className="bg-transparent text-[var(--ant-color-text)] text-[12px] font-medium transition-colors"
        style={{
          height: 28,
          border: '1px solid var(--ant-color-border)',
          borderRadius: 'var(--ant-border-radius, 10px)',
          padding: '0 10px',
          backgroundColor: 'var(--ant-color-bg-container)',
          outline: 'none',
          cursor: 'pointer',
        }}
      >
        <option value="all">
          {i18n.t('crm.features.features.deals.pages.dealkanbanpage.все_активности')}
        </option>
        <option value="due">
          {i18n.t('crm.features.features.deals.pages.dealkanbanpage.есть_следующий_шаг')}
        </option>
        <option value="missing">
          {i18n.t('crm.features.features.deals.pages.dealkanbanpage.без_следующего_шага')}
        </option>
      </select>

      {activeFilterCount > 0 ? (
        <Button
          type="text"
          size="small"
          onClick={() => setFilters(DEFAULT_DEAL_FILTERS)}
          style={{ color: 'var(--ant-color-error)' }}
        >
          {i18n.t('crm.features.features.deals.pages.dealkanbanpage.сбросить_фильтры')} (
          {activeFilterCount})
        </Button>
      ) : null}
    </div>
  );

  if (isLoading && viewMode !== 'list') {
    return (
      <main className="mx-auto h-full p-6" style={{ maxWidth: 900 }}>
        <div
          aria-busy="true"
          aria-label={i18n.t('crm.features.features.deals.pages.dealkanbanpage.загрузка_сделок')}
          style={LIST_SURFACE_STYLE}
        >
          <QuietListSkeleton rows={6} />
        </div>
      </main>
    );
  }

  if (isError && viewMode !== 'list') {
    return (
      <ErrorState
        variant="page"
        title={i18n.t(
          'crm.features.features.deals.pages.dealkanbanpage.не_удалось_загрузить_сделки',
        )}
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <ProjectWorkspaceSurface layout="workbench">
      <PageHeader
        title={i18n.t('crm.features.features.deals.pages.dealkanbanpage.сделки')}
        subtitle={
          /* Отбор считает сервер, поэтому «X из Y» здесь больше не бывает:
              загруженный список и есть результат фильтра. Слово склоняется
              по ru-правилам через tc(), число входит в перевод. */
          <span data-testid="deals-visible-count">
            {activeFilterCount > 0
              ? i18n.tc(
                  'crm.features.features.deals.pages.dealkanbanpage.сделок_по_фильтру',
                  deals.length,
                )
              : i18n.tc(
                  'crm.features.features.deals.pages.dealkanbanpage.сделок_кросс_проектный_вид',
                  deals.length,
                )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {/* View toggle — spec D `.seg` */}
            <div
              className="flex items-center overflow-hidden p-0.5"
              role="group"
              style={SEG_TRACK_STYLE}
            >
              <Tooltip title={i18n.t('crm.features.features.deals.pages.dealkanbanpage.канбан')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.deals.pages.dealkanbanpage.канбан_2')}
                  aria-pressed={viewMode === 'kanban'}
                  onClick={() => setViewMode('kanban')}
                  style={segOptionStyle(viewMode === 'kanban', true)}
                >
                  <Columns3 size={14} />
                </Button>
              </Tooltip>
              <Tooltip title={i18n.t('crm.features.features.deals.pages.dealkanbanpage.список')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.deals.pages.dealkanbanpage.список_2')}
                  aria-pressed={viewMode === 'list'}
                  onClick={() => setViewMode('list')}
                  style={segOptionStyle(viewMode === 'list', false)}
                >
                  <List size={14} />
                </Button>
              </Tooltip>
              <Tooltip title={i18n.t('crm.features.features.deals.pages.dealkanbanpage.карточки')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.deals.pages.dealkanbanpage.карточки_2')}
                  aria-pressed={viewMode === 'cards'}
                  onClick={() => setViewMode('cards')}
                  style={segOptionStyle(viewMode === 'cards', false)}
                >
                  <LayoutGrid size={14} />
                </Button>
              </Tooltip>
            </div>

            {viewMode === 'list' ? <GridColumnsButton profile={gridProfile} /> : null}

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openCreateForm('New')}
              style={LIST_CREATE_BUTTON_STYLE}
            >
              {i18n.t('crm.features.features.deals.pages.dealkanbanpage.новая_сделка')}
            </Button>
            <Tooltip
              title={i18n.t('crm.features.features.deals.pages.dealkanbanpage.обновить_сделки')}
            >
              <Button
                aria-label={i18n.t(
                  'crm.features.features.deals.pages.dealkanbanpage.обновить_сделки',
                )}
                icon={<ReloadOutlined />}
                onClick={() => void refetch()}
                style={LIST_ICON_BUTTON_STYLE}
              />
            </Tooltip>
          </div>
        }
        testId="deals-page-header"
      />

      <div className="flex-1 flex flex-col min-h-0 gap-3">
        {/* Revenue Goal & AI Forecast Bar */}
        <RevenueGoalBar deals={deals} />

        {/* Only the list uses the shared list filter surface. */}
        {viewMode === 'list' ? (
          <FilterBar search={filterSearch}>{filterControls}</FilterBar>
        ) : (
          <div
            className="crm-radius-m flex flex-col gap-3 px-3.5 py-2 border border-[var(--ant-color-border-secondary)] w-full shrink-0 md:flex-row md:items-center md:gap-4"
            style={{ backgroundColor: 'var(--ant-color-bg-container)' }}
          >
            {filterSearch}
            {filterControls}
          </div>
        )}

        {/* Мини-KPI: единая hairline-полоса (spec D `.kpis`), не три карточки. */}
        <div
          className="grid grid-cols-3 overflow-hidden"
          style={{
            background: 'var(--ant-color-border-secondary)',
            border: '1px solid var(--ant-color-border-secondary)',
            borderRadius: 'var(--ant-border-radius-lg, 14px)',
          }}
        >
          {(
            [
              {
                testId: 'deals-summary-total',
                label: i18n.t('crm.features.features.deals.pages.dealkanbanpage.всего_сделок'),
                value: deals.length,
              },
              {
                testId: 'deals-summary-active',
                label: i18n.t('crm.features.features.deals.pages.dealkanbanpage.активные'),
                value: activeDealsCount,
              },
              {
                testId: 'deals-summary-attention',
                label: i18n.t('crm.features.features.deals.pages.dealkanbanpage.требуют_внимания'),
                value: needsAttentionCount,
              },
            ] as const
          ).map((cell) => (
            <div
              key={cell.testId}
              className="min-w-0"
              style={{ background: 'var(--ant-color-bg-container)', padding: '12px 16px' }}
            >
              <div
                className="truncate"
                style={{
                  fontSize: 10.5,
                  fontWeight: 550,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: 'var(--ant-color-text-tertiary)',
                }}
              >
                {cell.label}
              </div>
              <div
                data-testid={cell.testId}
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: 'var(--ant-color-text)',
                  fontVariantNumeric: 'tabular-nums',
                  margin: '4px 0 2px',
                }}
              >
                {cell.value}
              </div>
            </div>
          ))}
        </div>

        {/* Selected bar */}
        {selectedIds.size > 0 ? (
          <Card
            size="small"
            style={{
              backgroundColor: 'var(--ant-color-primary-bg)',
              borderColor: 'var(--ant-color-primary-border)',
            }}
            styles={{
              body: { padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12 },
            }}
            data-testid="deals-bulk-bar"
          >
            <Text style={{ fontWeight: 600, color: 'var(--ant-color-primary)' }}>
              {selectedIds.size}{' '}
              {i18n.t('crm.features.features.deals.pages.dealkanbanpage.выбрано')}
            </Text>

            <select
              value={batchStage}
              onChange={(event) => setBatchStage(event.target.value as DealStage)}
              aria-label={i18n.t(
                'crm.features.features.deals.pages.dealkanbanpage.переместить_выбранные_сделки',
              )}
              style={{
                height: 28,
                border: '1px solid var(--ant-color-border)',
                borderRadius: 'var(--ant-border-radius-sm, 6px)',
                padding: '0 8px',
                fontSize: 12,
                backgroundColor: 'var(--ant-color-bg-container)',
                color: 'var(--ant-color-text)',
                outline: 'none',
                cursor: 'pointer',
                marginLeft: 8,
              }}
            >
              {STAGE_ORDER.map((stage) => (
                <option key={stage} value={stage}>
                  {getEnumLabel('dealStage', stage, i18n.t)}
                </option>
              ))}
            </select>

            <Button size="small" onClick={() => void handleBatchMove()} disabled={isBatchMoving}>
              {i18n.t('crm.features.features.deals.pages.dealkanbanpage.переместить')}
            </Button>
            <Button size="small" type="text" onClick={() => setSelectedIds(new Set())}>
              {i18n.t('crm.features.features.deals.pages.dealkanbanpage.отменить')}
            </Button>
            {batchError !== null ? <Text type="danger">{batchError}</Text> : null}
          </Card>
        ) : null}

        {/* Board content */}
        <div className="flex-1 min-h-0">
          {viewMode === 'kanban' ? (
            <KanbanBoard
              columns={columns}
              isUpdating={stageMutation.isPending}
              onCreateInStage={openCreateForm}
              onMoveDeal={(dealId: string, toStage: DealStage) => {
                if (toStage === 'Won') {
                  triggerCelebration();
                }
                stageMutation.mutate({ dealId, toStage });
              }}
              onToggleSelected={(dealId) =>
                setSelectedIds((current) => toggleSelection(current, dealId))
              }
              selectedIds={selectedIds}
            />
          ) : viewMode === 'cards' ? (
            <DealCardsGrid deals={deals} />
          ) : (
            <DealListView
              loading={isLoading}
              error={isError ? error : null}
              onRetry={() => void refetch()}
              deals={deals}
              isUpdating={stageMutation.isPending}
              onMoveDeal={(dealId: string, toStage: DealStage) => {
                if (toStage === 'Won') {
                  triggerCelebration();
                }
                stageMutation.mutate({ dealId, toStage });
              }}
              onSelectionChange={(dealIds) => setSelectedIds(new Set(dealIds))}
              onEdit={(deal) => setEditingDeal(deal)}
              onDelete={(deal) => deleteMutation.mutateAsync(deal.id)}
              deletePendingId={deleteMutation.isPending ? deleteMutation.variables : null}
              selectedIds={selectedIds}
              profile={gridProfile}
            />
          )}
        </div>
      </div>

      {/* Монтируем только открытой: иначе форма держала бы запросы справочников. */}
      {createStage !== null ? (
        <DealFormModal open initialStage={createStage} onClose={() => setCreateStage(null)} />
      ) : null}
      {editingDeal !== null ? (
        <DealFormModal open deal={editingDeal} onClose={() => setEditingDeal(null)} />
      ) : null}
    </ProjectWorkspaceSurface>
  );
}
